import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { splitBadgeId, getLangInfo } from '@/data/languages';

// Cert ID: KDSA-{learner_id_prefix}-{badge_id} — must match backend parser
export const buildCertId = (learnerId, badgeId) => {
  const prefix = (learnerId || '').split('-')[0] || 'unknown';
  return `KDSA-${prefix}-${badgeId}`;
};

export const getVerifyUrl = (certId) => `${window.location.origin}/verify/${certId}`;

const SIGNATURE = { signatureName: 'Kapil', signatureTitle: 'Instructor · DSA With Kapil' };

// Certificate tier definitions per base badge, localized for the course language
const TIER_BUILDERS = {
  'java-master': (L) => ({
    title: `Certificate of ${L} Mastery`,
    subtitle: `${L} Basics Mastery Program`,
    achievement: `has successfully completed all 10 ${L} Basics concept quizzes and demonstrated a strong foundation in ${L} programming fundamentals.`,
    seal: `${L.toUpperCase()} MASTER`,
    accentColor: [255, 184, 0],
    accentColor2: [255, 136, 0],
    ...SIGNATURE,
  }),
  'dsa-champion': (L) => ({
    title: 'Certificate of DSA Excellence',
    subtitle: `DSA Champion · ${L}`,
    achievement: `has successfully completed all 30 Data Structures & Algorithms problems in ${L} across Array, Two Pointer, Searching, Sorting, Recursion, Backtracking, Dynamic Programming, Graph, Trees, and String Algorithms modules.`,
    seal: `DSA CHAMPION ${L.toUpperCase()}`,
    accentColor: [0, 122, 255],
    accentColor2: [0, 100, 200],
    ...SIGNATURE,
  }),
  'full-stack-learner': (L) => ({
    title: 'Certificate of Complete Mastery',
    subtitle: `Full Stack Learner · ${L}`,
    achievement: `has demonstrated exceptional dedication by completing all 10 ${L} Basics quizzes AND all 30 Data Structures & Algorithms problems - achieving full mastery of the DSA With Kapil ${L} curriculum.`,
    seal: `FULL STACK ${L.toUpperCase()}`,
    accentColor: [168, 85, 247],
    accentColor2: [139, 92, 246],
    ...SIGNATURE,
  }),
};

export const getCertConfig = (badgeId) => {
  const { base, lang } = splitBadgeId(badgeId);
  const build = TIER_BUILDERS[base];
  return build ? { ...build(getLangInfo(lang).label), language: lang } : null;
};

export const isCertifiableBadge = (badgeId) => !!TIER_BUILDERS[splitBadgeId(badgeId).base];

export const CERTIFIABLE_BASES = Object.keys(TIER_BUILDERS);

const drawOrnament = (doc, cx, cy, r, color) => {
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setFillColor(color[0], color[1], color[2]);
  // Outer circle
  doc.setLineWidth(0.6);
  doc.circle(cx, cy, r, 'S');
  // Inner filled dot
  doc.circle(cx, cy, r * 0.35, 'F');
  // Cross rays
  doc.setLineWidth(0.4);
  doc.line(cx - r * 1.3, cy, cx - r * 0.55, cy);
  doc.line(cx + r * 0.55, cy, cx + r * 1.3, cy);
  doc.line(cx, cy - r * 1.3, cx, cy - r * 0.55);
  doc.line(cx, cy + r * 0.55, cx, cy + r * 1.3);
};

export const generateCertificatePDF = async (badgeId, learnerName, earnedDate, learnerId) => {
  const config = getCertConfig(badgeId);
  if (!config) return;

  const certId = buildCertId(learnerId, badgeId);
  const verifyUrl = getVerifyUrl(certId);

  // Generate QR code as data URL
  let qrDataUrl = null;
  try {
    qrDataUrl = await QRCode.toDataURL(verifyUrl, {
      margin: 1,
      width: 200,
      color: { dark: '#000000', light: '#FCF9F0' }
    });
  } catch (e) {
    console.error('QR generation failed:', e);
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageW = 297;
  const pageH = 210;
  const [aR, aG, aB] = config.accentColor;
  const [a2R, a2G, a2B] = config.accentColor2;

  // Background — off-white cream
  doc.setFillColor(252, 249, 240);
  doc.rect(0, 0, pageW, pageH, 'F');

  // Outer border (double line)
  doc.setDrawColor(aR, aG, aB);
  doc.setLineWidth(2);
  doc.rect(8, 8, pageW - 16, pageH - 16);
  doc.setLineWidth(0.5);
  doc.rect(12, 12, pageW - 24, pageH - 24);

  // Corner ornaments
  drawOrnament(doc, 20, 20, 4, config.accentColor);
  drawOrnament(doc, pageW - 20, 20, 4, config.accentColor);
  drawOrnament(doc, 20, pageH - 20, 4, config.accentColor);
  drawOrnament(doc, pageW - 20, pageH - 20, 4, config.accentColor);

  // Top small caps text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(a2R, a2G, a2B);
  doc.text('DSA WITH KAPIL', pageW / 2, 28, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text('Visual Learning Lab', pageW / 2, 33, { align: 'center' });

  // Main Title
  doc.setFont('times', 'bold');
  doc.setFontSize(38);
  doc.setTextColor(30, 30, 30);
  doc.text(config.title, pageW / 2, 60, { align: 'center' });

  // Decorative line under title
  doc.setDrawColor(aR, aG, aB);
  doc.setLineWidth(1);
  doc.line(pageW / 2 - 30, 65, pageW / 2 + 30, 65);
  doc.setFillColor(aR, aG, aB);
  doc.circle(pageW / 2, 65, 1.2, 'F');

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(a2R, a2G, a2B);
  doc.text(config.subtitle, pageW / 2, 74, { align: 'center' });

  // "This is to certify that"
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(13);
  doc.setTextColor(80, 80, 80);
  doc.text('This is to certify that', pageW / 2, 92, { align: 'center' });

  // Learner Name (huge)
  doc.setFont('times', 'bold');
  doc.setFontSize(42);
  doc.setTextColor(20, 20, 20);
  doc.text(learnerName, pageW / 2, 112, { align: 'center' });

  // Underline for name
  const nameWidth = doc.getTextWidth(learnerName);
  doc.setDrawColor(aR, aG, aB);
  doc.setLineWidth(0.6);
  doc.line(pageW / 2 - nameWidth / 2 - 4, 116, pageW / 2 + nameWidth / 2 + 4, 116);

  // Achievement text (word-wrapped)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  const lines = doc.splitTextToSize(config.achievement, pageW - 90);
  let y = 130;
  lines.forEach(line => {
    doc.text(line, pageW / 2, y, { align: 'center' });
    y += 6;
  });

  // Seal (circular badge on right)
  const sealX = pageW - 50;
  const sealY = pageH - 55;
  const sealR = 22;
  // Outer glow ring
  doc.setFillColor(aR, aG, aB);
  doc.circle(sealX, sealY, sealR + 1, 'F');
  // Inner white ring
  doc.setFillColor(252, 249, 240);
  doc.circle(sealX, sealY, sealR, 'F');
  // Middle colored disc
  doc.setFillColor(a2R, a2G, a2B);
  doc.circle(sealX, sealY, sealR - 4, 'F');
  // Center white
  doc.setFillColor(252, 249, 240);
  doc.circle(sealX, sealY, sealR - 10, 'F');
  // Seal text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(a2R, a2G, a2B);
  const sealLines = config.seal.split(' ');
  const startY = sealY - ((sealLines.length - 1) * 2);
  sealLines.forEach((line, idx) => {
    doc.text(line, sealX, startY + idx * 4, { align: 'center' });
  });

  // Signature block (left side)
  const sigX = 55;
  const sigY = pageH - 48;
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.4);
  doc.line(sigX - 25, sigY, sigX + 25, sigY);

  doc.setFont('times', 'italic');
  doc.setFontSize(18);
  doc.setTextColor(30, 30, 30);
  doc.text(config.signatureName, sigX, sigY - 3, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(config.signatureTitle, sigX, sigY + 5, { align: 'center' });

  // Date (right of signature)
  const dateX = pageW / 2;
  doc.setDrawColor(60, 60, 60);
  doc.line(dateX - 25, sigY, dateX + 25, sigY);
  doc.setFont('times', 'italic');
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 30);
  const formattedDate = earnedDate
    ? new Date(earnedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(formattedDate, dateX, sigY - 3, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('Date of Achievement', dateX, sigY + 5, { align: 'center' });

  // QR Code (bottom-left) linking to verify page
  if (qrDataUrl) {
    const qrSize = 22;
    const qrX = 20;
    const qrY = pageH - 44;
    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(a2R, a2G, a2B);
    doc.text('SCAN TO VERIFY', qrX + qrSize / 2, qrY + qrSize + 3, { align: 'center' });
  }

  // Certificate ID + verify URL (footer, small)
  doc.setFont('courier', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(`Certificate ID: ${certId}`, pageW / 2, pageH - 15, { align: 'center' });
  doc.text(`Verify: ${verifyUrl}`, pageW / 2, pageH - 11, { align: 'center' });

  // Save
  const safeName = (learnerName || 'learner').replace(/\s+/g, '-');
  doc.save(`Certificate-${badgeId.replace('--', '-')}-${safeName}.pdf`);
};
